<script setup lang="ts">
/**
 * PagedDoc — Word A4 페이지 단위 자동 분할.
 *
 * 흰 A4 배경 위에 슬롯 내용을 올리고, 직속 자식 블록이 페이지 콘텐츠 높이(PCH)를
 * 넘기면 margin-top 으로 다음 장 상단에 붙인다.
 *
 * 위치 측정은 offsetTop/getBoundingClientRect 대신 **높이 누적(usedInPage)** 을 쓴다.
 * (absolute flow · 스크롤 · 일부 브라우저 offsetParent 이슈에 강함)
 */
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

const A4H = 1123;
const MARGIN = 96;
const GAP = 24;
const PCH = A4H - MARGIN * 2; // 931
/** 한 페이지 콘텐츠 시작 → 다음 페이지 콘텐츠 시작 */
const STRIDE = A4H + GAP; // 1147

const root = ref<HTMLElement>();
const flow = ref<HTMLElement>();
const pageCount = ref(1);
let ro: ResizeObserver | null = null;
let mo: MutationObserver | null = null;
let raf = 0;
let timer = 0;

function collect(el: HTMLElement, out: HTMLElement[]) {
  for (const c of Array.from(el.children) as HTMLElement[]) {
    if (c.classList.contains('pg-flatten')) collect(c, out);
    else out.push(c);
  }
}

function clearSpacers(blocks: HTMLElement[]) {
  for (const b of blocks) {
    if (b.dataset.pgSpacer) {
      b.style.marginTop = '';
      delete b.dataset.pgSpacer;
    }
  }
}

function paginate() {
  const f = flow.value;
  if (!f) return;

  const blocks: HTMLElement[] = [];
  collect(f, blocks);
  clearSpacers(blocks);
  // 레이아웃 확정
  void f.offsetHeight;

  let usedInPage = 0;
  let pageIndex = 0;

  for (const b of blocks) {
    const height = Math.max(0, b.offsetHeight);
    if (height < 0.5) continue;

    // 현재 페이지에 이 블록을 이어 붙이면 넘침 → 다음 장으로
    if (usedInPage > 0.5 && usedInPage + height > PCH + 0.5) {
      const spacer = STRIDE - usedInPage;
      if (spacer > 0.5) {
        b.style.marginTop = `${spacer}px`;
        b.dataset.pgSpacer = '1';
      }
      pageIndex += 1;
      usedInPage = height;
    } else {
      usedInPage += height;
    }

    // 단일 블록이 한 페이지보다 큼 → 차지하는 추가 장수 반영
    if (usedInPage > PCH + 0.5) {
      const extra = Math.floor((usedInPage - 0.01) / PCH);
      pageIndex += extra;
      usedInPage = usedInPage - extra * PCH;
    }
  }

  pageCount.value = Math.max(1, pageIndex + 1);
}

function schedule() {
  cancelAnimationFrame(raf);
  raf = requestAnimationFrame(() => {
    paginate();
  });
}

/** 외부(autofill 등)에서 호출 — 레이아웃 안정화 후 2회 재분할 */
function relayout() {
  nextTick(() => {
    schedule();
    window.clearTimeout(timer);
    timer = window.setTimeout(() => schedule(), 50);
    window.setTimeout(() => schedule(), 200);
  });
}

defineExpose({ relayout, paginate });

onMounted(() => {
  nextTick(() => {
    paginate();
    if (!flow.value) return;
    ro = new ResizeObserver(() => schedule());
    ro.observe(flow.value);
    for (const c of Array.from(flow.value.children)) {
      if (c instanceof HTMLElement) ro.observe(c);
    }
    mo = new MutationObserver(() => {
      schedule();
      // 새로 생긴 자식도 관찰
      if (flow.value && ro) {
        for (const c of Array.from(flow.value.children)) {
          if (c instanceof HTMLElement) ro.observe(c);
        }
      }
    });
    mo.observe(flow.value, { childList: true, subtree: true, characterData: true });
  });
});

onBeforeUnmount(() => {
  ro?.disconnect();
  mo?.disconnect();
  cancelAnimationFrame(raf);
  window.clearTimeout(timer);
});
</script>

<template>
  <div ref="root" class="pgd">
    <div class="pgd-bg">
      <div v-for="p in pageCount" :key="p" class="pgd-page" />
    </div>
    <div ref="flow" class="pgd-flow">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.pgd {
  position: relative;
  width: 794px;
  max-width: 100%;
}
.pgd-bg {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.pgd-page {
  width: 100%;
  height: 1123px;
  background: #fff;
  border: 1px solid #e2e5ea;
  box-shadow: 0 4px 20px rgba(20, 30, 60, 0.09);
}
.pgd-flow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 96px;
  box-sizing: border-box;
}
</style>
